// src/utils/usageTracking.js
// R11: Track user search usage with IP-based protection
// Uses server-side tracking to prevent localStorage abuse

// Plan limits configuration (for reference/UI)
export const PLAN_LIMITS = {
  free: {
    searchesPerMonth: 100,
    alertsPerMonth: 0,
    aiRecommendations: 'basic',
    prioritySupport: false,
    adFree: false,
  },
  guest: {
    searchesPerMonth: 100, // Same as free, tracked by IP
    alertsPerMonth: 0,
    aiRecommendations: 'basic',
    prioritySupport: false,
    adFree: false,
  },
  basic: {
    searchesPerMonth: 250,
    alertsPerMonth: 10,
    aiRecommendations: 'basic',
    prioritySupport: false,
    adFree: false,
  },
  pro: {
    searchesPerMonth: Infinity,
    alertsPerMonth: 25,
    aiRecommendations: 'advanced',
    prioritySupport: true,
    adFree: false,
  },
  max: {
    searchesPerMonth: Infinity,
    alertsPerMonth: 100,
    aiRecommendations: 'premium',
    prioritySupport: true,
    adFree: true,
  },
};

// ============================================
// SERVER-SIDE TRACKING (IP + User ID)
// This is the SECURE method - can't be bypassed
// ============================================

/**
 * Check if user can search (calls server API)
 * Server tracks by IP for guests, by user_id for logged-in users
 * @param {Object|null} user - Supabase user object
 * @returns {Promise<{ canSearch: boolean, used: number, limit: number|string, remaining: number|string, plan: string }>}
 */
export const checkSearchLimit = async (user) => {
  try {
    const params = new URLSearchParams();
    if (user?.id) {
      params.append('userId', user.id);
    }

    const response = await fetch(`/api/track-search?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to check search limit');
    }

    const data = await response.json();
    
    return {
      canSearch: data.canSearch,
      used: data.used,
      limit: data.limit,
      remaining: data.remaining,
      plan: data.plan,
      percentage: data.limit === 'Unlimited' ? 0 : Math.round((data.used / data.limit) * 100)
    };

  } catch (error) {
    console.error('Error checking search limit:', error);
    // Fail open - allow search on error (better UX)
    return {
      canSearch: true,
      used: 0,
      limit: 100,
      remaining: 100,
      plan: 'guest',
      percentage: 0
    };
  }
};

/**
 * Record a search (calls server API)
 * Server increments counter based on IP (guests) or user_id (logged in)
 * @param {Object|null} user - Supabase user object
 * @param {Object} searchData - Optional search details for analytics
 * @returns {Promise<boolean>} Success status
 */
export const recordSearch = async (user, searchData = null) => {
  try {
    const response = await fetch('/api/track-search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user?.id || null,
        searchData: searchData ? {
          origin: searchData.origin,
          destination: searchData.destination,
          departure_date: searchData.departureDate,
          return_date: searchData.returnDate,
          passengers: searchData.passengers,
          travel_class: searchData.travelClass,
          is_round_trip: !!searchData.returnDate,
        } : null
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to record search');
    }

    return true;

  } catch (error) {
    console.error('Error recording search:', error);
    return false;
  }
};

/**
 * Get usage summary for display in UI
 * @param {Object|null} user - Supabase user object
 * @returns {Promise<Object>} Usage summary
 */
export const getUsageSummary = async (user) => {
  const searchStatus = await checkSearchLimit(user);
  const planLimits = PLAN_LIMITS[searchStatus.plan] || PLAN_LIMITS.free;

  return {
    plan: searchStatus.plan,
    searches: {
      used: searchStatus.used,
      limit: searchStatus.limit,
      remaining: searchStatus.remaining,
      percentage: searchStatus.percentage
    },
    features: planLimits,
    isUnlimited: searchStatus.limit === 'Unlimited' || planLimits.searchesPerMonth === Infinity
  };
};

// ============================================
// HELPER: Check and record in one call
// Use this in your search handler
// ============================================

/**
 * Check limit and record search if allowed
 * @param {Object|null} user - Supabase user object
 * @param {Object} searchData - Search details
 * @returns {Promise<{ allowed: boolean, usage: Object }>}
 */
export const checkAndRecordSearch = async (user, searchData = null) => {
  // First check if user can search
  const usage = await checkSearchLimit(user);

  if (!usage.canSearch) {
    return {
      allowed: false,
      usage,
      message: `You've reached your monthly limit of ${usage.limit} searches. Upgrade to continue.`
    };
  }

  // Record the search
  await recordSearch(user, searchData);

  // Return updated usage (increment locally for immediate UI update)
  return {
    allowed: true,
    usage: {
      ...usage,
      used: usage.used + 1,
      remaining: usage.remaining === 'Unlimited' ? 'Unlimited' : usage.remaining - 1,
      percentage: usage.limit === 'Unlimited' ? 0 : Math.round(((usage.used + 1) / usage.limit) * 100)
    }
  };
};

// ============================================
// LOCAL STORAGE FALLBACK (optional backup)
// Only used if server API fails
// ============================================

const STORAGE_KEY = 'airgati_guest_searches';

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const getLocalSearchCount = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return 0;

    const parsed = JSON.parse(data);
    const currentMonth = getCurrentMonthKey();

    if (parsed.month !== currentMonth) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ month: currentMonth, count: 0 }));
      return 0;
    }

    return parsed.count || 0;
  } catch {
    return 0;
  }
};

export const incrementLocalSearchCount = () => {
  try {
    const currentMonth = getCurrentMonthKey();
    const currentCount = getLocalSearchCount();
    const newCount = currentCount + 1;

    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      month: currentMonth,
      count: newCount
    }));

    return newCount;
  } catch {
    return 0;
  }
};
